import { useState, type ReactNode } from "react";
import { MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import type { SalveazaTargetareMapareCodRequest } from "@/api/types/targetareConfig";
import { cn } from "@/lib/utils";
import { ETICHETE_AMBIGUITATE } from "@/utils/targetareConfig";
import { formatNumar } from "@/utils/targetareFormatting";
import {
  AMBIGUITATE_MOSTENITA,
  AMBIGUITATI_COD,
  type RandMapare,
} from "@/utils/targetareRaportMapare";
import type { ColoanaTabel } from "@/utils/targetareTabel";
import type { FirmeCriteriuDeschis } from "../FirmeCriteriuDialog";

export type ContextMapare = {
  onSalveaza: (patch: SalveazaTargetareMapareCodRequest) => void;
  onFirme: (deschis: FirmeCriteriuDeschis) => void;
  inCurs: boolean;
};

const opreste = (e: { stopPropagation: () => void }) => e.stopPropagation();

function Explicatie({
  rand,
  onSalveaza,
}: {
  rand: RandMapare;
  onSalveaza: (text: string) => void;
}) {
  const [text, setText] = useState(rand.explicatie);
  return (
    <Popover onOpenChange={(v) => v && setText(rand.explicatie)}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex max-w-[320px] items-center gap-1 truncate rounded px-1 text-left text-xs text-muted-foreground hover:bg-muted"
          title={rand.explicatie || "Adaugă explicația"}
          onClick={opreste}
        >
          <MessageSquare className="h-3 w-3 shrink-0" />
          <span className="truncate">
            {rand.explicatie || "de ce se încadrează…"}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[480px] space-y-2"
        onClick={opreste}
      >
        <p className="text-xs text-muted-foreground">
          {rand.serviciu} · CAEN {rand.cod} ({rand.versiune_text}){" "}
          {rand.descriere}
        </p>
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={6}
          placeholder="De ce se încadrează (sau nu) codul la acest serviciu"
        />
        <Button size="sm" className="w-full" onClick={() => onSalveaza(text)}>
          Salvează explicația
        </Button>
      </PopoverContent>
    </Popover>
  );
}

export function celulaMapare(ctx: ContextMapare) {
  return (coloana: ColoanaTabel<RandMapare>, r: RandMapare): ReactNode => {
    if (!r.cod) {
      if (coloana.id === "sector" || coloana.id === "serviciu")
        return undefined;
      if (coloana.id === "explicatie")
        return (
          <span className="block truncate text-xs text-muted-foreground">
            {r.explicatie}
          </span>
        );
      return <span className="text-muted-foreground">–</span>;
    }
    const baza = {
      entity_id: r.entity_id,
      versiune: r.versiune,
      cod: r.cod,
      secundar: r.tip === "secundar",
    };
    switch (coloana.id) {
      case "cod":
        return (
          <button
            type="button"
            className={cn(
              "font-mono text-xs font-semibold hover:underline",
              !r.activ && "text-muted-foreground line-through",
              !r.valid && "text-destructive"
            )}
            onClick={(e) => {
              e.stopPropagation();
              ctx.onFirme({
                criteriu: {
                  tip: "caen",
                  cod: r.cod,
                  versiune: r.versiune,
                  fel: "oricare",
                },
                titlu: `Firme cu CAEN ${r.cod} (${r.versiune_text}) „${r.descriere}”, principal sau autorizat`,
              });
            }}
          >
            {r.cod}
          </button>
        );
      case "activ":
        return (
          <div onClick={opreste}>
            <Switch
              checked={r.activ}
              disabled={ctx.inCurs}
              onCheckedChange={(v) => ctx.onSalveaza({ ...baza, activ: v })}
            />
          </div>
        );
      case "ambiguitate_cod":
        return (
          <div onClick={opreste}>
            <Select
              value={r.ambiguitate_cod}
              disabled={ctx.inCurs}
              onValueChange={(v) =>
                ctx.onSalveaza({
                  ...baza,
                  ambiguitate: v === AMBIGUITATE_MOSTENITA ? "" : v,
                })
              }
            >
              <SelectTrigger className="h-7 w-[180px] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={AMBIGUITATE_MOSTENITA}>
                  moștenită:{" "}
                  {ETICHETE_AMBIGUITATE[r.ambiguitate_serviciu] ??
                    r.ambiguitate_serviciu}
                </SelectItem>
                {AMBIGUITATI_COD.map((t) => (
                  <SelectItem key={t} value={t}>
                    {ETICHETE_AMBIGUITATE[t] ?? t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );
      case "explicatie":
        return (
          <Explicatie
            rand={r}
            onSalveaza={(text) => ctx.onSalveaza({ ...baza, explicatie: text })}
          />
        );
      case "descriere":
        return (
          <span
            title={r.descriere}
            className={cn(
              "block truncate",
              !r.valid && "text-destructive",
              !r.activ && "text-muted-foreground"
            )}
          >
            {r.descriere}
          </span>
        );
      default:
        break;
    }
    const v = r[coloana.id];
    if (typeof v === "number")
      return <span className="tabular-nums">{formatNumar(v)}</span>;
    return undefined;
  };
}
